import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowUpRight, Clock3, Loader2 } from 'lucide-react';
import CtaBanner from '../components/CtaBanner';
import { fetchBlogPost } from '../api/blog';
import { buildBreadcrumbLd } from '../lib/structuredData';

const SITE_URL = 'https://residentialsupport.co.uk';

const Block = ({ block }) => {
  if (block.t === 'h')
    return (
      <h2 className="h-display text-2xl md:text-[1.7rem] text-[var(--rs-ink)] mt-10 mb-3 leading-snug">
        {block.c}
      </h2>
    );
  if (block.t === 'ul')
    return (
      <ul className="my-5 space-y-2 list-disc pl-6 text-[var(--rs-ink)]/80">
        {block.c.map((item, i) => (
          <li key={i} className="leading-relaxed">
            {item}
          </li>
        ))}
      </ul>
    );
  if (block.t === 'quote')
    return (
      <blockquote className="my-7 pl-5 border-l-4 border-[var(--rs-coral)] h-display text-xl md:text-2xl text-[var(--rs-ink)] leading-snug">
        {block.c}
      </blockquote>
    );
  return <p className="text-[var(--rs-ink)]/80 leading-relaxed text-[1.05rem] mt-4">{block.c}</p>;
};

const BlogPost = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setNotFound(false);
    setData(null);
    fetchBlogPost(id, { signal: controller.signal })
      .then((res) => {
        if (!res) {
          setNotFound(true);
        } else {
          setData(res);
        }
        setLoading(false);
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setNotFound(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [id]);

  if (notFound) return <Navigate to="/blog" replace />;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[var(--rs-ink)]/55" data-testid="blogpost-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading article…
      </div>
    );
  }

  const post = data?.post;
  const others = data?.related || [];
  if (!post) return <Navigate to="/blog" replace />;

  const url = `${SITE_URL}/blog/${post.id}`;
  const description = (post.excerpt || '').slice(0, 200);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description,
    image: post.image ? [post.image] : undefined,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    publisher: {
      '@type': 'Organization',
      name: 'Residential Support Ltd',
      url: SITE_URL,
    },
    articleSection: post.tag,
    inLanguage: 'en-GB',
  };

  return (
    <div>
      <Helmet>
        <title>{`${post.title} · Residential Support`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        {post.image ? <meta property="og:image" content={post.image} /> : null}
        <meta property="og:site_name" content="Residential Support" />
        <meta property="article:section" content={post.tag} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={description} />
        {post.image ? <meta name="twitter:image" content={post.image} /> : null}

        {/* JSON-LD Article schema */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(
            buildBreadcrumbLd([
              { label: 'Home', path: '/' },
              { label: 'Blog', path: '/blog' },
              { label: post.title, path: `/blog/${post.id}` },
            ])
          )}
        </script>
      </Helmet>

      {/* Article header */}
      <section className="bg-[var(--rs-cream)] border-b border-[#E7E0D2]">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 pt-12 lg:pt-16 pb-10">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-[var(--rs-green)] hover:text-[var(--rs-coral)]" data-testid="blogpost-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to all articles
          </Link>
          <div className="mt-6 flex items-center gap-3 text-xs text-[var(--rs-ink)]/65">
            <span className="px-2.5 py-1 rounded-full bg-[var(--rs-sand)] text-[var(--rs-ink)] font-medium">{post.tag}</span>
            <span className="inline-flex items-center gap-1">
              <Clock3 className="w-3.5 h-3.5" /> {post.readTime}
            </span>
          </div>
          <h1 className="h-display text-4xl md:text-5xl lg:text-6xl text-[var(--rs-ink)] mt-5 leading-[1.05]" data-testid="blogpost-title">
            {post.title}
          </h1>
          <p className="mt-5 text-[var(--rs-ink)]/70 text-lg max-w-2xl">{post.excerpt}</p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-[300px] md:h-[440px] object-cover rounded-[20px] shadow-md -mt-6 lg:-mt-10 relative z-10"
          />

          <article className="py-10 lg:py-14" data-testid="blogpost-body">
            {(post.body || []).map((block, i) => (
              <Block key={i} block={block} />
            ))}

            <div className="mt-12 pt-8 border-t border-[#E4DFF7]">
              <Link to="/contact" className="btn-primary">
                Talk to us about a service
              </Link>
            </div>
          </article>
        </div>
      </section>

      {/* Related posts */}
      {others.length > 0 && (
        <section className="sect bg-[var(--rs-cream)]">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="flex items-end justify-between gap-4 mb-8">
              <h2 className="h-display text-3xl md:text-4xl text-[var(--rs-ink)]">Keep reading</h2>
              <Link to="/blog" className="text-sm text-[var(--rs-coral)] inline-flex items-center gap-1 hover:gap-2 transition-all">
                All articles <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {others.map((p) => (
                <Link key={p.id} to={`/blog/${p.id}`} className="rs-card overflow-hidden lift group" data-testid={`blogpost-related-${p.id}`}>
                  <div className="h-44 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-[var(--rs-ink)]/55">
                      <span className="px-2.5 py-1 rounded-full bg-[var(--rs-sand)] text-[var(--rs-ink)] font-medium">{p.tag}</span>
                      <span>{p.readTime}</span>
                    </div>
                    <h3 className="h-display text-lg text-[var(--rs-ink)] mt-3 leading-snug">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaBanner />
    </div>
  );
};

export default BlogPost;
